import React, {useEffect} from 'react';
import {useForm, useFormState} from 'react-final-form';
import {useFieldArray} from 'react-final-form-arrays';
import {compose} from 'recompose';

import {getConfig} from 'units/calculator/api';
import {prepareConfig, checkServiceType, getPresetDefaultType} from './utils';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import withData from 'components/calculator/withData';
import CalculatorForm from 'components/calculator/CalculatorForm/CalculatorForm';
import ClosableSection from 'components/calculator/ClosableSection/ClosableSection';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import AddButton from 'components/Buttons/AddButton/AddButton';
import MDBFormItem, {
    MDBFormItemValues,
} from 'components/calculator/MDBForm/MDBFormItem/MDBFormItem';
import {MDBConfig, MDBPreparedConfig} from 'components/calculator/MDBForm/types';
import {CalculatorProps} from 'components/calculator/models';
import {ClusterType, getPlatformOptions, getDiskTypeOptions} from './constants';

export interface MDBFormValues {
    items: MDBFormItemValues[];
    [key: `items[${number}].${string}`]: unknown;
}

interface MDBFormOwnProps extends CalculatorProps {
    service: string;
}

interface MDBFormInnerProps {
    data: MDBPreparedConfig;
}

type MDBFormProps = MDBFormInnerProps & MDBFormOwnProps & WithTranslationProps;

const MDBForm: React.FC<MDBFormProps> = (props) => {
    const {title, service, data, i18nK, i18n} = props;

    const {
        diskTypes,
        defaultResources: {edition, generation, diskTypeId, diskSize, resourcePresetId},
    } = data;

    const {initialize} = useForm<MDBFormValues>();
    const {values, initialValues} = useFormState<MDBFormValues>();
    const {fields} = useFieldArray<MDBFormItemValues>('items');

    const createItemDefaultData = React.useCallback(() => {
        const type = getPresetDefaultType(resourcePresetId);
        const {minHosts} =
            diskTypes[`${resourcePresetId}==${diskTypeId}`] || Object.values(diskTypes)[0];
        let items: MDBFormItemValues = {
            edition,
            platform:
                getPlatformOptions(i18n).find((option) => option.value === generation)?.value || '',
            disk:
                getDiskTypeOptions(i18n).find((option) => option.value === diskTypeId)?.value || '',
            size: diskSize,
            presetId: resourcePresetId,
            hosts: minHosts,
            ip: false,
            traffic: 0,
            type,
        };

        if (checkServiceType(service, ClusterType.CLICKHOUSE)) {
            items = {...items, zookeeper: []};
        }
        return items;
    }, [diskSize, diskTypeId, diskTypes, edition, generation, i18n, resourcePresetId, service]);

    useEffect(() => {
        initialize({
            items: [createItemDefaultData()],
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!initialValues) {
        return null;
    }

    return (
        <CalculatorForm title={title}>
            <CalculatorFormLayout>
                {fields.map((name, index) => (
                    <CalculatorFormLayout.Section key={index}>
                        <ClosableSection
                            showClose={Number(fields.length) > 1}
                            onClose={() => fields.remove(index)}
                        >
                            <MDBFormItem
                                data={data}
                                service={service}
                                values={values.items[index]}
                                index={index}
                                name={name}
                            />
                        </ClosableSection>
                    </CalculatorFormLayout.Section>
                ))}
                <CalculatorFormLayout.Section>
                    <AddButton onClick={() => fields.push(createItemDefaultData())}>
                        {i18nK('mdb-button_add_cluster')}
                    </AddButton>
                </CalculatorFormLayout.Section>
            </CalculatorFormLayout>
        </CalculatorForm>
    );
};

const formDataLoader = async (clusterType: ClusterType, props: MDBFormProps) => {
    const {locale} = props;
    const data = (await getConfig(locale, clusterType)) as MDBConfig;
    return prepareConfig(data, clusterType) as MDBPreparedConfig;
};

export default (clusterType: ClusterType) =>
    compose<MDBFormProps, MDBFormOwnProps>(
        withData({loader: formDataLoader.bind(null, clusterType)}),
        withTranslation('calculator'),
    )(MDBForm);
